"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/src/lib/next-compat";
import { Award, ArrowRight, Download, Loader2 } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { CertificateSchema } from "@/src/api/types.gen";
import { downloadCertificatePdf } from "@/lib/certificate-display";

function formatDate(value?: string | null) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function StatusBadge({ status }: { status?: string | null }) {
    const isActive = status === "ACTIVE";
    const label = isActive ? "Ready" : status === "FAILED" ? "Failed" : "Preparing";
    const classes = isActive
        ? "bg-emerald-50 text-emerald-700"
        : status === "FAILED"
            ? "bg-red-50 text-red-700"
            : "bg-amber-50 text-amber-700";
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${classes}`}>
            {label}
        </span>
    );
}

export default function CertificatesList() {
    const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);

    const certificatesQuery = useQuery({
        queryKey: ["certificates-me"],
        queryFn: async () => {
            const { data, error, response } = await api.listUserCertificatesApiV1CertificatesMeGet();
            if (error) throw new Error(`Failed to fetch certificates (${response?.status})`);
            return (data ?? []) as CertificateSchema[];
        },
        staleTime: 30_000,
    });

    const certificates = certificatesQuery.data ?? [];

    const handleDownload = async (cert: CertificateSchema) => {
        if (!cert.uuid || downloadingUuid) return;
        setDownloadingUuid(cert.uuid);
        try {
            await downloadCertificatePdf(cert.uuid, cert.level_name);
        } catch (err) {
            console.error(err);
        } finally {
            setDownloadingUuid(null);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1 sm:mb-2">
                    Certificates
                </h1>
                <p className="text-sm sm:text-base text-slate-600">
                    Certificates you've earned for completing course levels.
                </p>
            </div>

            {certificatesQuery.isLoading ? (
                <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-2xl border border-slate-200 bg-white animate-pulse" />
                    ))}
                </div>
            ) : certificates.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-left">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
                        <Award className="h-5 w-5 text-gray-400" />
                    </div>
                    <p className="font-medium text-gray-700">No certificates yet</p>
                    <p className="mt-1 text-sm text-gray-500">
                        Complete a course level to earn your first certificate.
                    </p>
                    <Link
                        to="/courses"
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition"
                    >
                        Browse courses <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {certificates.map((cert) => (
                        <div
                            key={cert.uuid ?? cert.id}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
                        >
                            <div className="flex items-center gap-4 min-w-0">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50">
                                    <Award className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-slate-900 truncate">
                                        {cert.level_name} Level
                                    </p>
                                    <p className="text-sm text-slate-500 truncate">{cert.course_name}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        Completed {formatDate(cert.completion_date)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <StatusBadge status={cert.status} />
                                <button
                                    type="button"
                                    onClick={() => handleDownload(cert)}
                                    disabled={downloadingUuid === cert.uuid}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition disabled:opacity-60"
                                >
                                    {downloadingUuid === cert.uuid ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4" />
                                    )}
                                    Download
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
